import * as Sentry from '@sentry/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { APIError, APIErrorType, getAPIErrorMessage, useCreateCollectionMutation } from '@vizit/api';
import { useGetProductCategoriesQuery } from '@vizit/api';
import type { GetProductCategoriesQuery, GetProductCategoriesQueryVariables } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { useDebounce } from '@vizit/hooks';
import type { CategoryItem } from '@vizit/molecules';
import type { CollectionsFormSchema } from '@vizit/organisms';
import isEmpty from 'lodash.isempty';
import * as React from 'react';

const PAGE_SIZE = 10;

interface UseProductCategoryListProps {
  /** Organization ID */
  organizationId?: string;
  /** Portfolio ID */
  workspaceId?: string;
  /** Function to handle authentication error */
  onAuthenticationError: (error: APIError) => void;
}

/**
 * Result interface for the product category list hook
 */
interface UseProductCategoryListResult {
  /** Array of category items with transformed breadcrumbs */
  items: CategoryItem[];
  /** Whether the initial data is loading */
  isLoading: boolean;
  /** Whether the next page is being fetched */
  isFetchingNextPage: boolean;
  /** Whether there are more pages to load */
  hasNextPage: boolean;
  /** Current filter text for searching categories */
  filterText: string;
  /** Function to update the filter text */
  setFilterText: (text: string) => void;
  /** Function to load more items (next page) */
  loadMore: () => void;
}

interface UseCollectionNewDialogProps {
  /** Portfolio ID */
  workspaceId?: string;
  /** Organization ID */
  organizationId?: string;
  /** Function to call when the collection is successfully created */
  onSuccessfulCreate: (collectionId: string) => void;
  /** Function to handle authentication error */
  onAuthenticationError: (error: APIError) => void;
}

/**
 * Hook for managing collection creation
 * Handles form submission and error states
 *
 * @param props - Hook configuration
 * @returns Object containing submission state and handlers
 */
function useCollectionNewDialog(props: UseCollectionNewDialogProps) {
  const { workspaceId, organizationId, onSuccessfulCreate, onAuthenticationError } = props;

  const [submissionErrorMessage, setSubmissionErrorMessage] = React.useState<string | null>(null);

  const { mutateAsync: createCollection } = useCreateCollectionMutation();

  const handleOnSubmit = async (data: CollectionsFormSchema) => {
    try {
      setSubmissionErrorMessage(null);

      const response = await createCollection({
        workspace_id: workspaceId ?? '',
        name: data.name,
        product_category_id: data.category,
        image_type: data.imageType,
      });

      if (!response?.id) {
        throw new Error('Failed to create collection');
      }

      Sentry.addBreadcrumb({
        message: 'Collection created successfully.',
        level: 'info',
        data: {
          collection_id: response.id,
          organization_id: organizationId,
          workspace_id: workspaceId,
          name: data.name,
          category: data.category,
          image_type: data.imageType,
        },
      });

      onSuccessfulCreate(response.id);
    } catch (error) {
      if (
        error instanceof APIError &&
        (error.type === APIErrorType.NotAuthenticated || error.type === APIErrorType.NotAuthorized)
      ) {
        onAuthenticationError(error);
      } else {
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Collection,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Collection].collectionCreate,
          },
          extra: {
            organization_id: organizationId,
            workspace_id: workspaceId,
            collection_name: data.name,
            category: data.category,
            image_type: data.imageType,
          },
        });

        setSubmissionErrorMessage(
          getAPIErrorMessage(error, 'Error creating collection. Please try again in a few minutes.'),
        );
      }
    }
  };

  return {
    submissionErrorMessage,
    onSubmit: handleOnSubmit,
  };
}

/**
 * Hook for managing product category list with infinite scrolling and filtering
 * Uses TanStack Query for proper caching and background updates
 *
 * @param props - Hook configuration
 * @returns Object containing category data and control functions
 */
// TODO: this is a duplicate of the useProductCategoryList hook in the products package
// Share this code between the two packages.
function useProductCategoryList(props: UseProductCategoryListProps): UseProductCategoryListResult {
  const { organizationId, workspaceId, onAuthenticationError } = props;

  const [localFilterText, setLocalFilterText] = React.useState('');
  const debouncedFilterText = useDebounce(localFilterText, 300);

  const queryVariables: Omit<GetProductCategoriesQueryVariables, 'offset'> = React.useMemo(() => {
    return {
      // @ts-ignore: this is protected by the router and will always have a value.
      organization_id: organizationId,
      // @ts-ignore: this is protected by the router and will always have a value.
      workspace_id: workspaceId,
      breadcrumbs_like: isEmpty(debouncedFilterText) ? undefined : debouncedFilterText,
      limit: PAGE_SIZE,
      name_like: isEmpty(debouncedFilterText) ? undefined : debouncedFilterText,
      sort_by: 'name',
      sort_direction: 'asc',
    };
  }, [organizationId, workspaceId, debouncedFilterText]);

  const { data, error, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['GetProductCategoriesPages', queryVariables],
    queryFn: async ({ pageParam = 0 }) => {
      const response: GetProductCategoriesQuery = await useGetProductCategoriesQuery.fetcher({
        ...queryVariables,
        offset: pageParam,
      });

      const transformedCategories: CategoryItem[] = (response.data || []).map(
        (category: GetProductCategoriesQuery['data'][number]) => ({
          ...category,
          breadcrumbs: category.breadcrumbs.split(','),
        }),
      );

      const hasData = response.data && response.data.length > 0;

      return {
        data: transformedCategories,
        // Only increment cursor if we received data, otherwise return undefined to indicate no more data
        nextCursor: hasData ? pageParam + 10 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
    enabled: !!workspaceId,
  });

  // Flatten all pages into a single items array with a duplicate ID check
  const items = React.useMemo(() => {
    const allPageItems = data?.pages.flatMap((page) => page.data) || [];

    const uniqueItems = allPageItems.reduce((uniqueItems, item) => {
      if (uniqueItems.findIndex((existingItem) => existingItem.id === item.id) === -1) {
        uniqueItems.push(item);
      }
      return uniqueItems;
    }, [] as CategoryItem[]);

    return uniqueItems;
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  React.useEffect(() => {
    if (error instanceof APIError && error.type === APIErrorType.NotAuthenticated) {
      onAuthenticationError(error);
    }
  }, [error, onAuthenticationError]);

  return {
    items,
    isLoading: isLoading,
    isFetchingNextPage: isFetchingNextPage,
    hasNextPage: hasNextPage || false,
    filterText: localFilterText,
    setFilterText: setLocalFilterText,
    loadMore,
  };
}

export { useCollectionNewDialog, useProductCategoryList };
